import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Calendar, LayoutDashboard, Users, Settings, Bell, LogOut, Menu, X, History, Monitor, Shield, TrendingUp } from 'lucide-react';
import axios from 'axios';
import { ErrorBoundary } from './ErrorBoundary';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function Layout({ children }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [config, setConfig] = useState(null);

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/config`);
      setConfig(data);
    } catch (error) {
      console.error('Error fetching config:', error);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  // Filtrado por PERMISOS (no por rol hardcodeado). Dashboard y Calendario
  // son accesibles para cualquier usuario autenticado (coinciden con el
  // comportamiento previo). El resto se muestra según los permisos reales que
  // el backend devolvió en `user.permissions` — así los roles custom
  // (ej. "Administración") ven exactamente los módulos que su rol tiene
  // asignados en la colección `roles`.
  const perms = Array.isArray(user?.permissions) ? user.permissions : [];
  const hasPerm = (needed) => {
    if (!needed || needed.length === 0) return true;
    return needed.some((p) => perms.includes(p));
  };

  const menuItems = [
    { path: '/dashboard', icon: LayoutDashboard, label: 'Dashboard', anyPermission: [] },
    { path: '/calendar',  icon: Calendar,        label: 'Calendario', anyPermission: [] },
    { path: '/management', icon: TrendingUp, label: 'Gerencia',           anyPermission: ['management.view'] },
    { path: '/users',      icon: Users,       label: 'Usuarios',          anyPermission: ['user.manage'] },
    { path: '/roles',      icon: Shield,      label: 'Roles y Permisos',  anyPermission: ['role.manage'] },
    { path: '/audit',      icon: History,     label: 'Historial',         anyPermission: ['audit.view'] },
    { path: '/settings',   icon: Settings,    label: 'Configuración',
      anyPermission: ['config.manage', 'state.manage', 'reminder.manage', 'api_keys.manage', 'payment_log.sync'] },
    { path: '/tv-panel',   icon: Monitor,     label: 'Pantallas TV',      anyPermission: ['tv.manage'] },
  ];

  const visibleMenuItems = menuItems.filter((item) => hasPerm(item.anyPermission));

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-50 w-[280px] bg-[#FDFBF7] border-r border-[#E8E5DD] transform transition-transform duration-200 lg:translate-x-0 overflow-y-auto pt-[calc(4rem+env(safe-area-inset-top))] lg:pt-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
        style={{
          paddingBottom: 'env(safe-area-inset-bottom)',
          paddingLeft: 'env(safe-area-inset-left)',
        }}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="p-6 border-b border-[#E8E5DD]">
            <h1 className="text-2xl font-bold text-[#2B4C3B]">
              {config?.clinic_name || 'Clínica Agenda'}
            </h1>
            <p className="text-sm text-[#6A736B] mt-1">{user?.name}</p>
            <span className="inline-block mt-2 px-2.5 py-0.5 bg-[#F3EBE1] text-[#2B4C3B] text-xs font-semibold rounded-full" data-testid="sidebar-role-badge">
              {/* `role_label` viene del backend (colección `roles`, consultada fresca
                   en cada login y en /auth/me). Si por cualquier razón falta, caemos
                   al key de rol capitalizado — nunca a un valor hardcoded distinto. */}
              {user?.role_label || (user?.role ? user.role.charAt(0).toUpperCase() + user.role.slice(1) : '—')}
            </span>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-2">
            {visibleMenuItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  data-testid={`nav-${item.label.toLowerCase()}`}
                  onClick={() => setSidebarOpen(false)}
                  className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors duration-200 ${
                    isActive
                      ? 'bg-[#2B4C3B] text-white'
                      : 'text-[#6A736B] hover:bg-[#F3EBE1] hover:text-[#2B4C3B]'
                  }`}
                >
                  <Icon size={20} strokeWidth={1.5} />
                  <span className="font-medium">{item.label}</span>
                </Link>
              );
            })}
          </nav>

          {/* Footer */}
          <div className="p-4 border-t border-[#E8E5DD]">
            <button
              data-testid="logout-button"
              onClick={handleLogout}
              className="flex items-center gap-3 w-full px-4 py-3 text-[#A65C5C] hover:bg-red-50 rounded-lg transition-colors duration-200"
            >
              <LogOut size={20} strokeWidth={1.5} />
              <span className="font-medium">Cerrar sesión</span>
            </button>
          </div>
        </div>
      </aside>

      {/* Mobile header */}
      <div
        className="lg:hidden fixed top-0 left-0 right-0 z-[60] bg-[#FFFFFF] border-b border-[#E8E5DD] px-4 py-3 flex items-center justify-between"
        style={{
          paddingTop: 'calc(0.75rem + env(safe-area-inset-top))',
          paddingLeft: 'calc(1rem + env(safe-area-inset-left))',
          paddingRight: 'calc(1rem + env(safe-area-inset-right))',
        }}
      >
        <button
          data-testid="mobile-menu-toggle"
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className="p-2 hover:bg-[#F3EBE1] rounded-lg transition-colors active:bg-[#E8E5DD] touch-manipulation"
          type="button"
          aria-label={sidebarOpen ? 'Cerrar menú' : 'Abrir menú'}
        >
          {sidebarOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
        <h2 className="text-lg font-bold text-[#2B4C3B]">
          {config?.clinic_name || 'Clínica Agenda'}
        </h2>
        <div className="w-10" />
      </div>

      {/* Main content */}
      <main
        className="lg:ml-[280px] pt-[calc(4rem+env(safe-area-inset-top))] lg:pt-0"
        style={{
          paddingLeft: 'env(safe-area-inset-left)',
          paddingRight: 'env(safe-area-inset-right)',
          paddingBottom: 'env(safe-area-inset-bottom)',
        }}
      >
        <ErrorBoundary>{children}</ErrorBoundary>
      </main>

      {/* Overlay for mobile */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/20 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </div>
  );
}